import type { DiscoveryApi, FetchApi } from "@backstage/core-plugin-api";

import type { ExplorerApi } from "./ExplorerApi";
import type {
  AssignmentLookupRequest,
  AssignmentLookupResponse,
  AssignmentsListResponse,
  CachedCloudExplorerResponse,
  CloudProvider,
  EntityAssetsResponse,
  EntityAssignmentCostsResponse,
  ExplorerAccountAssetsResponse,
  ExplorerAccountsResponse,
  ExplorerAssetApiDetail,
} from "./types";

export class ExplorerClient implements ExplorerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl("cloudhealth-explorer");
  }

  private async assertOk(response: Response): Promise<void> {
    if (response.ok) {
      return;
    }
    let detail = `${response.status} ${response.statusText}`;
    try {
      const bodyText = await response.text();
      if (bodyText) {
        try {
          const body = JSON.parse(bodyText) as { error?: string };
          detail = body.error
            ? `${detail}: ${body.error}; body=${bodyText}`
            : `${detail}: body=${bodyText}`;
        } catch {
          detail = `${detail}: ${bodyText}`;
        }
      }
    } catch {
      // Ignore unreadable error responses.
    }
    throw new Error(`CloudHealth Explorer API error: ${detail}`);
  }

  async getCloud(cloud: CloudProvider): Promise<CachedCloudExplorerResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/explorer/cloud/${cloud.toLowerCase()}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  async getAccounts(cloud: CloudProvider): Promise<ExplorerAccountsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/explorer/accounts/${cloud.toLowerCase()}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  async getAccountAssets(
    cloud: CloudProvider,
    accountId: string,
    displayName: string,
  ): Promise<ExplorerAccountAssetsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/explorer/assets/${cloud.toLowerCase()}/${encodeURIComponent(accountId)}?displayName=${encodeURIComponent(displayName)}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  // ─── Phase 2: Account-level assignments ───────────────────────────────

  async getAssignments(options?: {
    nodeRef?: string;
    entityRef?: string;
    limit?: number;
  }): Promise<AssignmentsListResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.nodeRef) params.set("nodeRef", options.nodeRef);
    if (options?.entityRef) params.set("entityRef", options.entityRef);
    if (options?.limit) params.set("limit", String(options.limit));
    const qs = params.toString();
    const url = `${baseUrl}/v1/assignments${qs ? `?${qs}` : ""}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  async deleteAssignment(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/assignments/${encodeURIComponent(id)}`;
    const response = await this.fetchApi.fetch(url, {
      method: "DELETE",
    });
    await this.assertOk(response);
  }

  async getEntityAssets(entityRef: string): Promise<EntityAssetsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/entities/assets?entityRef=${encodeURIComponent(entityRef)}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  async lookupAssignments(
    request: AssignmentLookupRequest,
  ): Promise<AssignmentLookupResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/assignments/lookup`;
    const response = await this.fetchApi.fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
    await this.assertOk(response);
    return response.json();
  }

  // ─── Phase 5: FinOps costs by assignment ───────────────────────────────

  async getEntityCosts(
    entityRef: string,
  ): Promise<EntityAssignmentCostsResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/entities/costs?entityRef=${encodeURIComponent(entityRef)}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }

  // ─── Phase 6: Single asset detail ────────────────────────────────────────

  async getAssetDetails(nodeRef: string): Promise<ExplorerAssetApiDetail> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/explorer/asset?nodeRef=${encodeURIComponent(nodeRef)}`;
    const response = await this.fetchApi.fetch(url);
    await this.assertOk(response);
    return response.json();
  }
}
